import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiHelper from '../utils/apiHelper';
import './AlertModal.css';

/**
 * AlertModal Component - Shows critical Alert notifications automatically
 * Forces users to see important alerts without needing to click notification bell
 * 
 * @param {boolean} props.isOpen Whether the alert modal is open
 * @param {Array} props.alerts Array of alert notifications to display
 * @param {Function} props.onClose Callback function to close the modal
 * @param {Function} props.onDismiss Callback when alert is dismissed
 * @param {string} props.userEmail User's email address for API calls
 * @returns {JSX.Element} AlertModal component
 */
function AlertModal({ isOpen, alerts, onClose, onDismiss, userEmail }) {
  const navigate = useNavigate();
  const [currentAlertIndex, setCurrentAlertIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Auto-advance to next alert after delay if multiple alerts
  useEffect(() => {
    if (!isOpen || !alerts || alerts.length <= 1) return;

    const timer = setTimeout(() => {
      if (currentAlertIndex < alerts.length - 1) {
        setCurrentAlertIndex(prev => prev + 1);
      }
    }, 8000); // Show each alert for 8 seconds

    return () => clearTimeout(timer);
  }, [isOpen, alerts, currentAlertIndex]);

  /**
   * Handle alert dismissal
   */
  const handleDismiss = async (markAsRead = true) => {
    if (isProcessing || !alerts || alerts.length === 0) return;

    setIsProcessing(true);
    const currentAlert = alerts[currentAlertIndex];

    try {
      if (markAsRead && userEmail && !currentAlert.isRead) {
        await apiHelper.markNotificationRead(currentAlert.id, userEmail);
      }

      // If there are more alerts, show the next one
      if (currentAlertIndex < alerts.length - 1) {
        setCurrentAlertIndex(prev => prev + 1);
      } else {
        // All alerts shown, close modal
        if (onDismiss) {
          onDismiss();
        }
        setCurrentAlertIndex(0);
      }
    } catch (error) {
      console.error('Error dismissing alert:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Handle action button click
   */
  const handleActionClick = async () => {
    const currentAlert = alerts[currentAlertIndex];
    
    // Mark as read
    await handleDismiss(true);

    // Navigate to action URL if provided
    if (currentAlert.actionUrl) {
      navigate(currentAlert.actionUrl);
    }
  };

  /**
   * Get priority-based CSS class
   */
  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'High': return 'alert-priority-high';
      case 'Medium': return 'alert-priority-medium';
      case 'Low': return 'alert-priority-low';
      default: return 'alert-priority-high'; // Default alerts to high priority
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (!isOpen || !alerts || alerts.length === 0) return null;

  const currentAlert = alerts[currentAlertIndex];
  const hasMultipleAlerts = alerts.length > 1;

  return (
    <div className="alert-modal-overlay">
      <div className={`alert-modal-content ${getPriorityClass(currentAlert.priority)}`}>
        {/* Alert Header */}
        <div className="alert-modal-header">
          <div className="alert-icon-container">
            <i className="fas fa-exclamation-triangle alert-icon"></i>
            <span className="alert-type">ALERT</span>
          </div>
          <div className="alert-priority">
            {currentAlert.priority} Priority
          </div>
        </div>

        {/* Alert Body */}
        <div className="alert-modal-body">
          <h2 className="alert-title">{currentAlert.title}</h2>
          
          <div className="alert-timestamp">
            <i className="fas fa-clock"></i>
            {formatDate(currentAlert.created)}
          </div>

          <div 
            className="alert-message"
            dangerouslySetInnerHTML={{ __html: currentAlert.message }}
          />

          {/* Alert Counter for Multiple Alerts */}
          {hasMultipleAlerts && (
            <div className="alert-counter">
              Alert {currentAlertIndex + 1} of {alerts.length}
            </div>
          )}
        </div>

        {/* Alert Footer */}
        <div className="alert-modal-footer">
          {currentAlert.actionUrl ? (
            <div className="alert-actions-two">
              <button 
                className="alert-btn alert-btn-secondary"
                onClick={() => handleDismiss(true)}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Processing...
                  </>
                ) : (
                  'Dismiss'
                )}
              </button>
              <button 
                className="alert-btn alert-btn-primary"
                onClick={handleActionClick}
                disabled={isProcessing}
              >
                <i className="fas fa-arrow-right"></i>
                View Details
              </button>
            </div>
          ) : (
            <div className="alert-actions-single">
              <button 
                className="alert-btn alert-btn-primary alert-btn-full"
                onClick={() => handleDismiss(true)}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Processing...
                  </>
                ) : (
                  'I Understand'
                )}
              </button>
            </div>
          )}
        </div>

        {/* Progress Bar for Multiple Alerts */}
        {hasMultipleAlerts && (
          <div className="alert-progress-container">
            <div 
              className="alert-progress-bar"
              style={{
                width: `${((currentAlertIndex + 1) / alerts.length) * 100}%`
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default AlertModal; 